import { SITE_NAME, SITE_ORIGIN } from './site.js';
import { buildBlogPostSeo } from './blogSeo.js';
export const BLOG_POST_JSON_LD_ID = 'blog-post-jsonld';
export const BLOG_FAQ_JSON_LD_ID = 'blog-faq-jsonld';
/**
 * Every block id an article page owns. Client-side navigation reuses one
 * `<head>`, so the code that applies these has to know the full set to clear —
 * otherwise the previous article's schema outlives the page it described.
 */
export const BLOG_JSON_LD_IDS = [BLOG_POST_JSON_LD_ID, BLOG_FAQ_JSON_LD_ID];
/**
 * JSON-LD is injected as the body of a `<script>` tag, so the payload must not
 * be able to close that tag or open a comment. Escaping the three characters
 * that can (`<`, `>`, `&`) keeps the JSON valid while making `</script>` in a
 * post title inert. The home schema is static copy and skips this.
 */
function serializeJsonLd(value) {
    return JSON.stringify(value)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
}
/** Depth-first walk of a TipTap document, yielding every node. */
function walkNodes(node, visit) {
    if (!node || typeof node !== 'object')
        return;
    const typed = node;
    visit(typed);
    if (Array.isArray(typed.content)) {
        for (const child of typed.content)
            walkNodes(child, visit);
    }
}
/**
 * `BlogPosting` schema for a single published post. Built from the same
 * resolved metadata as the `<head>` tags, so the structured data can't drift
 * from what the page advertises to social crawlers.
 */
export function buildBlogPostJsonLd(post, origin = SITE_ORIGIN) {
    const seo = buildBlogPostSeo(post, origin);
    return serializeJsonLd({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: seo.headline,
        ...(seo.description ? { description: seo.description } : {}),
        ...(seo.image ? { image: seo.image } : {}),
        ...(seo.publishedTime ? { datePublished: seo.publishedTime } : {}),
        ...(seo.modifiedTime ? { dateModified: seo.modifiedTime } : {}),
        author: { '@type': 'Person', name: seo.authorName },
        publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            logo: { '@type': 'ImageObject', url: `${origin}/logo.svg` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': seo.canonical },
        url: seo.canonical,
    });
}
/**
 * `FAQPage` schema built from every `faqAccordion` block in the post body.
 * Returns null when the post has no answered questions, so we never emit an
 * empty `mainEntity` (which Search Console flags).
 */
export function buildBlogFaqJsonLd(content) {
    const questions = [];
    walkNodes(content, (node) => {
        if (node.type !== 'faqAccordion')
            return;
        const items = node.attrs?.items;
        if (!Array.isArray(items))
            return;
        for (const item of items) {
            const question = typeof item?.question === 'string' ? item.question.trim() : '';
            const answer = typeof item?.answer === 'string' ? item.answer.trim() : '';
            if (question && answer)
                questions.push({ question, answer });
        }
    });
    if (questions.length === 0)
        return null;
    return serializeJsonLd({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions.map(({ question, answer }) => ({
            '@type': 'Question',
            name: question,
            acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
    });
}
/**
 * Every `<script type="application/ld+json">` block a post page should carry,
 * ready for the prerender pass to inject. Bundling them behind one function
 * keeps the SSR entry free of re-exported constants, which the react-refresh
 * lint rule reads as component exports.
 */
export function buildBlogJsonLdBlocks(post, origin = SITE_ORIGIN) {
    const faq = buildBlogFaqJsonLd(post.content);
    return [
        { id: BLOG_POST_JSON_LD_ID, json: buildBlogPostJsonLd(post, origin) },
        ...(faq ? [{ id: BLOG_FAQ_JSON_LD_ID, json: faq }] : []),
    ];
}
